import { yupResolver } from '@hookform/resolvers/yup';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useGetPoolOptions } from 'components/admin-portal/Tables/Settings/PlanUnits/components/utils/useGetPoolOptions';
import { ControlledAutoComplete } from 'components/shared/ControlledFields';
import PlanUnitSelector from 'components/shared/PlanUnitSelector';
import dayjs from 'dayjs';
import { useDispatch, useRosterFunctions, useSelector } from 'hooks';
import { useGetClusters } from 'queries';
import { useEffect, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { setUserPreference } from 'redux/actionCreators';
import useGetEmployeeList from '../hooks/useGetEmployeeList';
import useGetRequestReason from '../hooks/useGetRequestReason';
import { filterSchema } from '../schema';
import FilterButton from './FilterButton';
import SearchButton from './SearchButton';
import ShowMoreFilters from './ShowMoreFilters';
import StartAndEndDate from './StartAndEndDate';

import { StyledFilterBarBox } from './FilterBar.styles';

import type { PlanUnit } from 'components/types';
import type { Filters } from '../types';

const defaultValues = {
  costCenter: null,
  startDate: dayjs(new Date())
    .subtract(1, 'month')
    .startOf('day')
    .toISOString(),
  endDate: dayjs(new Date()).endOf('day').toISOString(),
  cluster: null,
  pool: null,
  employee: null,
  requestReason: null,
  rosterFunction: null
};

const StyledBox = styled(Box)({
  flexGrow: 1,
  marginTop: '8px',
  marginBottom: '8px',
  minWidth: 200,
  maxWidth: 400
});

interface Props {
  onSearch: () => void;
  onFiltersChange: (filters: Filters) => void;
  hasPoolsOrgGrant: boolean;
  hasClustersOrgGrant: boolean;
}

const FilterBar = ({
  onFiltersChange,
  onSearch,
  hasPoolsOrgGrant,
  hasClustersOrgGrant
}: Props) => {
  'use memo';

  const { t } = useTranslation('common');
  const { control, setValue, formState } = useForm<Filters>({
    resolver: yupResolver(filterSchema),
    mode: 'onChange',
    defaultValues: {
      costCenter: null,
      startDate: dayjs(new Date())
        .subtract(1, 'month')
        .startOf('day')
        .toISOString(), // Default to one month ago
      endDate: dayjs(new Date()).endOf('day').toISOString(), // Default to today
      cluster: null,
      pool: null,
      employee: null,
      requestReason: null,
      rosterFunction: null
    }
  });

  const filterHasErrors: boolean = Object.keys(formState.errors).length > 0;

  const [showMoreFilters, setShowMoreFilters] = useState(false);

  const { data: clusters = [] } = useGetClusters();
  const { data: pools = [] } = useGetPoolOptions();

  const { data: employeeListData } = useGetEmployeeList();

  // order employeeList by nameInformal
  const employeeList = employeeListData?.sort((a, b) =>
    a.nameInformal.localeCompare(b.nameInformal, 'en', {
      sensitivity: 'base'
    })
  );

  const { data: requestReasonList } = useGetRequestReason();
  const { rosterFunctions: rosterFunctionOptions } = useRosterFunctions();

  const dispatch = useDispatch();
  const { preferences } = useSelector((state) => state);

  const flexLastUsedCostCenterSelection =
    preferences.flexLastUsedCostCenterSelection;
  const flexShouldSaveCostCenterSelection =
    preferences.flexShouldSaveCostCenterSelection;
  const [costCenterList, setCostCenterList] = useState<PlanUnit[]>([]);

  const saveSelection = (value: (string | number)[]) => {
    dispatch(
      setUserPreference({
        key: 'flexLastUsedCostCenterSelection',
        value
      })
    );
  };

  // NOTE: useWatch (not watch()) — watch() reads form state imperatively and is
  // not reactive under React Compiler memoization ('use memo' on this
  // component), which froze the date range so search kept the initial month
  // (issue #2049). useWatch subscribes through the render cycle and is
  // compiler-safe.
  const formData = useWatch({ control });

  const {
    startDate,
    endDate,
    cluster,
    pool,
    employee,
    reason,
    rosterFunction
  } = formData;

  useEffect(() => {
    const selectedCostCenterIds = costCenterList?.map((el) => el.uniqId) || [];

    onFiltersChange({
      costCenterId: selectedCostCenterIds as number[],
      startDate: dayjs(startDate, 'DD-MM-YYYY').isValid()
        ? dayjs(startDate, 'DD-MM-YYYY').startOf('day').toISOString()
        : null,
      endDate: dayjs(endDate, 'DD-MM-YYYY').isValid()
        ? dayjs(endDate, 'DD-MM-YYYY').endOf('day').toISOString()
        : null,
      clusterId: cluster?.clusterId ?? null,
      poolId: pool?.value ?? null,
      employeeId: employee?.empId ?? null,
      requestReason: reason?.name ?? null,
      functionId: rosterFunction?.id ?? null
    });
  }, [
    cluster?.clusterId,
    costCenterList,
    employee?.empId,
    endDate,
    onFiltersChange,
    pool?.value,
    reason?.name,
    rosterFunction?.id,
    startDate
  ]);

  const handleMoreFilters = () => {
    setShowMoreFilters(!showMoreFilters);
  };

  const onSelectedCostCenter = (value: PlanUnit[]) => {
    setCostCenterList(value);
    flexShouldSaveCostCenterSelection &&
      saveSelection(value.map(({ id }) => id));
  };

  const defaultSelectedCostCenter =
    flexLastUsedCostCenterSelection?.map(
      (uniqId) =>
        ({
          uniqId
        }) as PlanUnit
    ) || undefined;

  useEffect(() => {
    setValue('startDate', dayjs(new Date()).startOf('month').toISOString());
    setValue('endDate', dayjs(new Date()).endOf('month').toISOString());
  }, [setValue]);

  return (
    <>
      <StyledFilterBarBox>
        <Box
          display="flex"
          flexWrap="wrap"
          rowGap={0}
          columnGap={2}
          justifyContent="start"
          alignItems="center"
          width="100%"
          maxWidth={1536}
          marginRight="auto"
        >
          <Box flexGrow={0} maxWidth={400} marginBottom={2} marginRight={2}>
            <PlanUnitSelector
              hideGroups
              doNotPersist
              onSelected={onSelectedCostCenter}
              useEmployeePlanunits
              onClear={() => onSelectedCostCenter([])}
              defaultValue={defaultSelectedCostCenter}
            />
          </Box>
          <StartAndEndDate {...{ control, defaultValues }} />
          {hasClustersOrgGrant && (
            <StyledBox>
              <ControlledAutoComplete
                disableSorting
                optionLabelProp="name"
                idProperty="clusterId"
                variant="outlined"
                control={control}
                name="cluster"
                label={t('cluster')}
                options={clusters ?? []}
                fullWidth
              />
            </StyledBox>
          )}
          {hasPoolsOrgGrant && (
            <StyledBox>
              <ControlledAutoComplete
                disableSorting
                optionLabelProp="label"
                idProperty="value"
                variant="outlined"
                control={control}
                name="pool"
                label={t('Pools')}
                options={pools ?? []}
                fullWidth
              />
            </StyledBox>
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            marginTop: (theme) => theme.spacing(4),
            gap: (theme) => theme.spacing(2)
          }}
        >
          <FilterButton {...{ handleMoreFilters, showMoreFilters }} />
          <SearchButton {...{ onSearch }} disabled={filterHasErrors} />
        </Box>
      </StyledFilterBarBox>
      {showMoreFilters && (
        <ShowMoreFilters
          {...{
            control,
            employeeList,
            requestReasonList,
            rosterFunctionOptions
          }}
        />
      )}
    </>
  );
};

export default FilterBar;
